import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Chip,
  Stack,
  Alert,
  Button,
  CardActionArea,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  AccountTree as TreeIcon,
} from '@mui/icons-material';
import SearchFilters from '../../components/Search/SearchFilters';
import setupService from '../../services/setup.service';
import { Setup } from '../../types';
import { toast } from 'react-toastify';

const SearchSetups: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [setups, setSetups] = useState<Setup[]>([]);
  const [filteredSetups, setFilteredSetups] = useState<Setup[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copying, setCopying] = useState<number | null>(null);

  // Load public setups
  useEffect(() => {
    const loadSetups = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await setupService.getAvailable();
        setSetups(data);

        // Extract unique tags
        const tags = new Set<string>();
        data.forEach((setup: Setup) => {
          setup.tags?.forEach((tag: string) => tags.add(tag));
        });
        setAvailableTags(Array.from(tags).sort());
      } catch (err) {
        console.error('Error loading setups:', err);
        setError('Erreur lors du chargement des setups');
      } finally {
        setLoading(false);
      }
    };

    loadSetups();
  }, []);

  // Filter setups
  useEffect(() => {
    let filtered = [...setups];

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (setup) =>
          setup.name?.toLowerCase().includes(query) ||
          setup.created_by_name?.toLowerCase().includes(query)
      );
    }

    // Filter by tags
    if (selectedTags.length > 0) {
      filtered = filtered.filter((setup) =>
        selectedTags.every((tag) => setup.tags?.includes(tag))
      );
    }

    setFilteredSetups(filtered);
  }, [searchQuery, selectedTags, setups]);

  // Copy setup
  const handleCopy = async (setup: Setup) => {
    setCopying(setup.id);
    try {
      await setupService.copy(setup.id);
      toast.success(`Setup "${setup.name}" copié dans votre atelier`);
    } catch (error) {
      console.error('Error copying setup:', error);
      toast.error('Erreur lors de la copie du setup');
    } finally {
      setCopying(null);
    }
  };

  // Count nodes recursively
  const countNodes = (nodes: any[]): number => {
    let count = 0;
    for (const node of nodes) {
      count++;
      if (node.children) {
        count += countNodes(node.children);
      }
    }
    return count;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Left Panel - Search Filters */}
        <Grid item xs={12} md={3}>
          <SearchFilters
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            selectedTags={selectedTags}
            onTagsChange={setSelectedTags}
            availableTags={availableTags}
            contentType="setups"
          />
        </Grid>

        {/* Right Panel - Results */}
        <Grid item xs={12} md={9}>
          <Box>
            {/* Results count */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {filteredSetups.length} setup{filteredSetups.length > 1 ? 's' : ''} trouvé{filteredSetups.length > 1 ? 's' : ''}
            </Typography>

            {/* Results */}
            <Grid container spacing={2}>
              {filteredSetups.map((setup) => (
                <Grid item xs={12} sm={6} md={4} key={setup.id}>
                  <Card>
                    <CardActionArea>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                          <TreeIcon color="primary" />
                          <Typography variant="h6" sx={{ flex: 1 }}>
                            {setup.name}
                          </Typography>
                        </Box>

                        {setup.created_by_name && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Par {setup.created_by_name}
                          </Typography>
                        )}

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {countNodes(setup.nodes || [])} nœuds
                        </Typography>

                        {setup.tags && setup.tags.length > 0 && (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5} sx={{ mb: 2 }}>
                            {setup.tags.map((tag) => (
                              <Chip key={tag} label={tag} size="small" variant="outlined" />
                            ))}
                          </Stack>
                        )}

                        <Button
                          variant="outlined"
                          startIcon={copying === setup.id ? <CircularProgress size={16} /> : <CopyIcon />}
                          onClick={() => handleCopy(setup)}
                          disabled={copying !== null}
                          fullWidth
                          size="small"
                        >
                          Copier dans mon atelier
                        </Button>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {filteredSetups.length === 0 && (
              <Box textAlign="center" py={4}>
                <TreeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  Aucun setup ne correspond à vos critères de recherche
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SearchSetups;
